import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import ToolSeoHero from "../components/tools-seo/ToolSeoHero";
import ToolSeoBenefits from "../components/tools-seo/ToolSeoBenefits";
import ToolSeoFAQ from "../components/tools-seo/ToolSeoFAQ";
import ToolSeoCTA from "../components/tools-seo/ToolSeoCTA";
import SeoHead from "../components/seo/SeoHead";
import StructuredData, { buildFAQSchema } from "../components/seo/StructuredData";

const THEME_KEY = "hookora_theme";

const scriptFaqs = [
  {
    question: "What is a short script generator?",
    answer:
      "A short script generator is a tool that helps creators shape ideas into short-form scripts more quickly. It is useful when you want clearer structure and faster writing support."
  },
  {
    question: "Who is Hookora’s Short Script Generator for?",
    answer:
      "It is built for creators, marketers, brands, and anyone producing short-form content who wants a faster and cleaner scripting workflow."
  },
  {
    question: "Can I use this for TikTok, Reels, and Shorts?",
    answer:
      "Yes. Hookora is designed to support short-form creator workflows, including scripts for TikTok, Instagram Reels, and YouTube Shorts."
  },
  {
    question: "Why do short scripts matter?",
    answer:
      "Even short content performs better when the message is clear. A better script helps improve flow, delivery, and overall content quality."
  },
  {
    question: "Does Hookora only generate scripts?",
    answer:
      "No. Hookora also includes tools for hooks, titles, captions, hashtags, descriptions, and thumbnail text."
  },
  {
    question: "Is this useful if I already know what I want to say?",
    answer:
      "Yes. Hookora is still useful because it helps turn rough thoughts into cleaner, more structured wording that is easier to record or publish."
  }
];

function ShortScriptGeneratorPage() {
  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const pageStyle = useMemo(
    () => ({
      background: isDark ? "#020617" : "#f8fafc",
      backgroundImage: isDark
        ? "radial-gradient(circle at top left, rgba(59,130,246,0.12), transparent 28%), radial-gradient(circle at top right, rgba(124,58,237,0.10), transparent 24%)"
        : "none",
      minHeight: "100vh"
    }),
    [isDark]
  );

  return (
    <div style={pageStyle}>
      <SeoHead
        title="Short Script Generator | Hookora"
        description="Use Hookora’s Short Script Generator to turn ideas into clearer short-form scripts faster with better structure, flow, and content direction."
        path="/short-script-generator"
      />
      <StructuredData
        id="short-script-generator-faq-schema"
        data={buildFAQSchema(scriptFaqs)}
      />

      <Navbar />
      <ToolSeoHero
        badge="Hookora Tool • Short Script Generator"
        title="Short Script Generator for creators who want faster, clearer short-form scripts"
        description="The Hookora Short Script Generator helps creators turn content ideas into usable short-form scripts faster. Instead of struggling to structure your thoughts, you get clearer script direction with a stronger hook, smoother body, and more intentional CTA."
        highlights={[
          "Write faster",
          "Improve structure",
          "Create with more confidence",
          "Built for creator workflow"
        ]}
        primaryCtaLabel="Start Free"
        primaryCtaTo="/register"
        secondaryCtaLabel="See Features"
        secondaryCtaTo="/features"
        previewTitle="What the Short Script Generator helps you do"
        previewItems={[
          {
            label: "Structure",
            text: "Turn rough content ideas into scripts with a clearer beginning, middle, and end."
          },
          {
            label: "Speed",
            text: "Move from idea to usable script faster without getting stuck on what comes next."
          },
          {
            label: "Flow",
            text: "Create short scripts that feel more natural, focused, and easier to record."
          }
        ]}
      />

      <ToolSeoBenefits
        eyebrow="Why creators use it"
        title="A short script generator should help you create faster without making your content feel flat"
        description="Short-form content moves fast, but a stronger script still matters. Hookora helps you build clearer script flow so your content feels sharper, easier to follow, and more intentional."
        benefits={[
          {
            title: "Turn ideas into scripts faster",
            text: "When you know the topic but not the exact wording, Hookora helps you move forward without staring at a blank screen."
          },
          {
            title: "Create better hook-to-CTA flow",
            text: "A stronger short script usually starts with a better opening and ends with a clearer next step. Hookora helps shape both."
          },
          {
            title: "Reduce creative friction",
            text: "Many creators waste time trying to figure out how to phrase simple ideas. Hookora helps reduce that delay."
          },
          {
            title: "Keep your message clearer",
            text: "A short script works best when each line pushes the idea forward. Hookora helps make that structure easier to build."
          },
          {
            title: "Useful for short-form creator workflows",
            text: "Whether you create for TikTok, Instagram Reels, YouTube Shorts, or broader social content, better short scripts improve delivery."
          },
          {
            title: "Fits into a wider content system",
            text: "Your script works better when it connects with your hook, title, caption, and thumbnail. Hookora supports that wider flow."
          }
        ]}
      />

      <ToolSeoFAQ
        title="Short Script Generator FAQ"
        intro="Here are a few common questions about Hookora’s Short Script Generator."
        faqs={scriptFaqs}
      />

      <ToolSeoCTA
        badge="Start free with Hookora"
        title="Use Hookora’s Short Script Generator to write stronger short-form scripts with less friction"
        description="If you want faster scripting, clearer structure, and a smoother short-form content workflow, Hookora gives you a practical place to start."
        primaryLabel="Create Free Account"
        primaryTo="/register"
        secondaryLabel="Explore Features"
        secondaryTo="/features"
      />
    </div>
  );
}

export default ShortScriptGeneratorPage;